/* eslint-disable */
// @ts-nocheck
// temporary deprecated
import { cx } from '@linaria/core';
import { useMaskito } from '@maskito/react';
import mergeRefs from 'merge-refs';
import { ChangeEvent, useEffect, useState } from 'react';
import { FieldErrors, useFormContext } from 'react-hook-form';

import { mapCardInfoByBin } from '~/pages/pay/hooks/useBinacle/getCardInfo.ts';
import { CardInfo } from '~/pages/pay/hooks/useBinacle/types.ts';
import { useBinacle } from '~/pages/pay/hooks/useBinacle/useBinacle.ts';
import { FormType } from '~/pages/pay/ui/PayPage.scheme.ts';
import {
  extractBin8,
  extractMaskedLast4,
  normalizeCardNumber,
} from '~/shared/lib/cardNumber.ts';
import { checkLuhn } from '~/shared/lib/checkLuhn.ts';
import { usePreviousValue } from '~/shared/lib/hooks/usePreviousValue.ts';
import { mapCardBrandBySlug } from '~/shared/lib/mapCardBrandBySlug.tsx';
import { Nullable } from '~/shared/lib/types/utils.ts';
import { Input } from '~/shared/ui/Input/Input.tsx';
import { InputField } from '~/shared/ui/InputField/InputField.tsx';
import { Spinner } from '~/shared/ui/Spinner/Spinner.tsx';

import { fieldName } from '../../../../../../constants/paymentCard.ts';
import { SbpButton } from '../../../../SbpButton/SbpButton.tsx';

import { emailMask, expiredDateMask, nameMask } from './Form.constants.ts';
import { CardFormData } from './Form.types.ts';
import {
  classNameInputCardNumber,
  classNameInputDisabled,
  classNameInputSlave,
  classNameIsRevealedCardNumber,
  classNameRootInputCardNumber,
  InputBlock,
  InputCardNumber,
  MaskedCardNumber,
  singleInput,
  StyledForm,
  StyledSpinnerContainer,
} from './Form.ui.ts';
import { createCardNumberMask } from './helpers/createCardNumberMask.ts';
import { createSecureCodeMask } from './helpers/createSecureCodeMask.ts';
import { useLocalization } from './localization/localization.ts';

type FormProps = {
  defaultValue?: Partial<CardFormData>;
  disabledFields?: Partial<Record<keyof CardFormData, boolean>>;
  isCardExisting?: boolean;
  isUnchangeable?: boolean;
};

// ToDo - пока DEPRECATED
export const Form = (props: FormProps) => {
  const {
    defaultValue,
    disabledFields,
    isCardExisting = false,
    isUnchangeable = false,
  } = props;

  const { t } = useLocalization();

  const {
    data: binacleData,
    getCardInfo,
    isLoading: isLoadingCardInfo,
    resetCardInfo,
  } = useBinacle();

  const [bin, setBin] = useState<Nullable<string>>(
    defaultValue?.cardNumber ? extractBin8(defaultValue.cardNumber) : null,
  );

  const prevBin = usePreviousValue(bin);

  const cardData =
    isCardExisting && bin
      ? mapCardInfoByBin[bin]
      : (binacleData ?? ({} as CardInfo));
  const {
    // ToDo в первой версии договорились, что cvv всегда 3 цифры
    cardInfo: { brand: cardBrand, cardNumberLength, secureCodeLength = 3 } = {},
  } = cardData;

  // Маски полей.
  const inputCardNumberMaskRef = useMaskito({
    options: createCardNumberMask(cardNumberLength),
  });
  const inputCardholderMaskRef = useMaskito({ options: nameMask });
  const inputEmailMaskRef = useMaskito({ options: emailMask });
  const inputExpiredDateMaskRef = useMaskito({ options: expiredDateMask });
  const inputCvv3MaskRef = useMaskito({
    options: createSecureCodeMask(secureCodeLength ?? 3),
  });

  const form = useFormContext<FormType>();
  const {
    formState: { errors, isValid, isSubmitting, isLoading },
    register,
    setFocus,
    trigger,
    watch,
    setValue,
  } = form;

  const getCardFieldError = (fieldPath: keyof FormType['cardData']) => {
    const cardErrors = errors.cardData as
      | FieldErrors<FormType['cardData']>
      | undefined;

    return Boolean(cardErrors?.[fieldPath]);
  };

  const watchedCardData = watch('cardData');
  const {
    cardNumber: cardNumberValue,
    expirationDate: expirationDateValue,
    secureCode: secureCodeValue,
  } = watchedCardData || {};

  const [isRevealedCardNumber, setIsRevealedCardNumber] = useState(
    !defaultValue?.cardNumber,
  );

  // Очистка offer при размонтировании
  useEffect(() => {
    return () => setValue('offer', false);
  }, [setValue]);

  useEffect(() => {
    if (bin === prevBin || isCardExisting) {
      return;
    }

    if (bin) {
      getCardInfo(bin);
    } else {
      resetCardInfo();
    }
  }, [bin, getCardInfo, prevBin, resetCardInfo, isCardExisting]);

  const inputNumber = register(`cardData.${fieldName.CARD_NUMBER}`, {
    onBlur: () => {
      if (
        isRevealedCardNumber &&
        cardNumberValue?.length === cardNumberLength &&
        checkLuhn(cardNumberValue || '')
      ) {
        setIsRevealedCardNumber(false);
      }
    },
    setValueAs: normalizeCardNumber,
  });
  const inputNumberRef = mergeRefs<HTMLInputElement>(
    inputNumber.ref,
    inputCardNumberMaskRef,
  );
  const isInvalidInputNumber = getCardFieldError(fieldName.CARD_NUMBER);

  const handleFocusCardNumber = () => {
    setIsRevealedCardNumber(true);
  };

  useEffect(() => {
    if (isCardExisting) {
      setFocus(`cardData.${fieldName.EXPIRATION_DATE}`);
      return;
    }
    const newBin = extractBin8(cardNumberValue);

    setBin(newBin);

    if (cardNumberValue?.length === cardNumberLength) {
      if (!checkLuhn(cardNumberValue)) {
        trigger(`cardData.${fieldName.CARD_NUMBER}`);

        return;
      }

      setIsRevealedCardNumber(false);
      setFocus(`cardData.${fieldName.EXPIRATION_DATE}`);
    }
  }, [
    bin,
    cardNumberLength,
    cardNumberValue,
    setFocus,
    trigger,
    isCardExisting,
  ]);

  const inputExpiredDate = register(`cardData.${fieldName.EXPIRATION_DATE}`);
  const inputExpiredDateRef = mergeRefs<HTMLInputElement>(
    inputExpiredDate.ref,
    inputExpiredDateMaskRef,
  );
  const isInvalidInputExpiredDate = getCardFieldError(
    fieldName.EXPIRATION_DATE,
  );

  useEffect(() => {
    if (expirationDateValue?.length === 5) {
      if (secureCodeLength) {
        setFocus(`cardData.${fieldName.SECURE_CODE}`);
      } else {
        setFocus(`cardData.${fieldName.CARDHOLDER}`);
      }
    }
  }, [expirationDateValue?.length, secureCodeLength, setFocus]);

  const [inputSecurityCodeType, setInputSecurityCodeType] = useState<
    'password' | 'tel'
  >('password');

  const inputSecurityCode = register(`cardData.${fieldName.SECURE_CODE}`, {
    onBlur: () => {
      setInputSecurityCodeType('password');
    },
  });
  const inputSecurityCodeRef = mergeRefs<HTMLInputElement>(
    inputSecurityCode.ref,
    inputCvv3MaskRef,
  );
  const isInvalidInputSecurityCode = getCardFieldError(fieldName.SECURE_CODE);

  const handleFocusInputSecurityCode = () => {
    setInputSecurityCodeType('tel');
  };

  useEffect(() => {
    if (secureCodeValue?.length === secureCodeLength) {
      setFocus(`cardData.${fieldName.CARDHOLDER}`);
    }
  }, [secureCodeLength, secureCodeValue?.length, setFocus]);

  const inputCardholder = register(`cardData.${fieldName.CARDHOLDER}`);
  const inputCardholderRef = mergeRefs<HTMLInputElement>(
    inputCardholder.ref,
    inputCardholderMaskRef,
  );

  const inputEmail = register(`cardData.${fieldName.EMAIL}`);
  const inputEmailRef = mergeRefs<HTMLInputElement>(
    inputEmail.ref,
    inputEmailMaskRef,
  );

  const isInvalidInputCardHolder = getCardFieldError(fieldName.CARDHOLDER);
  const isInvalidInputEmail = getCardFieldError(fieldName.EMAIL);

  useEffect(() => {
    return () => {
      setValue('offer', false);
    };
  }, [setValue]);

  const cardDataError = (() => {
    const localizedBinacleError = errors.root?.binacle
      ? t('Card is not supported')
      : null;

    const cardErrors = errors.cardData as
      | FieldErrors<FormType['cardData']>
      | undefined;
    const firstError = [
      cardErrors?.[fieldName.CARD_NUMBER],
      cardErrors?.[fieldName.EXPIRATION_DATE],
      cardErrors?.[fieldName.SECURE_CODE],
      cardErrors?.[fieldName.CARDHOLDER],
      cardErrors?.[fieldName.EMAIL],
    ].find(Boolean);

    const errorMessage = firstError?.message;

    return (
      localizedBinacleError ??
      (typeof errorMessage === 'string' ? errorMessage : errorMessage)
    );
  })();

  return (
    <StyledForm>
      <InputField error={cardDataError}>
        <InputCardNumber
          className={cx(isRevealedCardNumber && classNameIsRevealedCardNumber)}
        >
          <MaskedCardNumber>
            {extractMaskedLast4(cardNumberValue)}
          </MaskedCardNumber>
          <Input
            {...inputNumber}
            aria-label={t('cardNumberPlaceholder')}
            autoComplete="cc-number"
            autoCorrect="off"
            inputClassName={cx(
              classNameInputCardNumber,
              isUnchangeable && classNameInputDisabled,
            )}
            inputMode="numeric"
            disabled={disabledFields?.cardNumber}
            isInvalid={isInvalidInputNumber}
            onChange={inputNumber.onChange}
            onInput={inputNumber.onChange}
            onFocus={handleFocusCardNumber}
            placeholder={t('cardNumberPlaceholder')}
            prefixIcon={
              isLoadingCardInfo ? (
                <StyledSpinnerContainer>
                  <Spinner />
                </StyledSpinnerContainer>
              ) : (
                mapCardBrandBySlug(cardBrand)
              )
            }
            ref={inputNumberRef}
            rootClassName={classNameRootInputCardNumber}
            spellCheck={false}
            type="tel"
          />
          <InputBlock>
            <Input
              {...inputExpiredDate}
              aria-label={t('datePlaceholder')}
              autoComplete="cc-exp"
              autoCorrect="off"
              inputClassName={cx(
                classNameInputSlave,
                isUnchangeable && classNameInputDisabled,
              )}
              inputMode="numeric"
              disabled={disabledFields?.expirationDate}
              isInvalid={isInvalidInputExpiredDate}
              onInput={inputExpiredDate.onChange}
              placeholder={t('datePlaceholder')}
              ref={inputExpiredDateRef}
              spellCheck={false}
              type="tel"
            />
            <Input
              {...inputSecurityCode}
              aria-label={t('cvvPlaceholder')}
              autoComplete="cc-csc"
              autoCorrect="off"
              inputClassName={classNameInputSlave}
              inputMode="numeric"
              isInvalid={isInvalidInputSecurityCode}
              onFocus={handleFocusInputSecurityCode}
              onInput={inputSecurityCode.onChange}
              placeholder={t('cvvPlaceholder')}
              ref={inputSecurityCodeRef}
              spellCheck={false}
              type={inputSecurityCodeType}
            />
          </InputBlock>
          <Input
            {...inputCardholder}
            aria-label={t('ownerNamePlaceholder')}
            autoComplete="cc-name"
            autoCorrect="off"
            label={t('NameOnCard')}
            inputClassName={classNameInputSlave}
            isInvalid={isInvalidInputCardHolder}
            rootClassName={singleInput}
            onInput={(event: ChangeEvent<HTMLInputElement>) => {
              event.target.value = event.target.value.toUpperCase();
              inputCardholder.onChange(event);
            }}
            placeholder={t('ownerNamePlaceholder')}
            ref={inputCardholderRef}
          />
          <Input
            {...inputEmail}
            aria-label={t('ownerEmailPlaceholder')}
            autoComplete="email"
            autoCorrect="off"
            label={t('emailLabel')}
            inputClassName={classNameInputSlave}
            isInvalid={isInvalidInputEmail}
            rootClassName={singleInput}
            onInput={(event: ChangeEvent<HTMLInputElement>) => {
              event.target.value = event.target.value.toLowerCase();
              inputEmail.onChange(event);
            }}
            placeholder={t('emailPlaceholder')}
            ref={inputEmailRef}
          />
        </InputCardNumber>
      </InputField>
      <SbpButton
        disabled={!isValid || isSubmitting || isLoading}
        isLoading={isSubmitting || isLoading}
        type="submit"
      >
        {t('Pay')}
      </SbpButton>
    </StyledForm>
  );
};
